<template>
    <div>
        <button 
            type="button" 
            class="btn btn-sm btn-outline-theme"
            @click="openModal"
        >
            <i class="bi bi-download me-1"></i>
            Download Schedules
        </button>

        <!-- Modal -->
        <div class="modal fade" ref="modalRef" tabindex="-1" aria-hidden="true">
            <div class="modal-dialog modal-xl">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title">Export Scheduled Services</h5>
                        <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                    </div>
                    <div class="modal-body pt-1">
                        <!-- Filters & Search -->
                        <div class="row mb-3" style="position: relative; z-index: 1050;">
                            <!-- Date Range Filter -->
                            <div class="col-md-3">
                                <label class="form-label">Date Range</label>
                                <vue-multiselect
                                    v-model="selectedDateRangeOption"
                                    :options="dateRangeOptions"
                                    :searchable="false"
                                    :allow-empty="false"
                                    label="label"
                                    track-by="value"
                                    placeholder="Select date range"
                                    :show-labels="false"
                                ></vue-multiselect>
                            </div>
                            <!-- Month Selection -->
                            <div class="col-md-3" v-if="selectedDateRangeOption && selectedDateRangeOption.value === 'choose_month'">
                                <label class="form-label">Select Month</label>
                                <vue-multiselect
                                    v-model="selectedMonth"
                                    :options="monthOptions"
                                    :searchable="false"
                                    :allow-empty="false"
                                    label="label"
                                    track-by="label"
                                    placeholder="Select Month"
                                    :show-labels="false"
                                ></vue-multiselect>
                            </div>
                            <!-- Custom Date Range -->
                            <div class="col-md-3" v-if="selectedDateRangeOption && selectedDateRangeOption.value === 'custom'">
                                <label class="form-label">Start Date</label>
                                <VueDatePicker
                                    v-model="startDate"
                                    :enable-time-picker="false"
                                    format="dd-MM-yyyy"
                                    model-type="yyyy-MM-dd"
                                    auto-apply
                                    :clearable="true"
                                    placeholder="Select Start Date"
                                    input-class-name="form-control form-control-sm"
                                    :teleport="true"
                                />
                            </div>
                            <div class="col-md-3" v-if="selectedDateRangeOption && selectedDateRangeOption.value === 'custom'">
                                <label class="form-label">End Date</label>
                                <VueDatePicker
                                    v-model="endDate"
                                    :enable-time-picker="false"
                                    format="dd-MM-yyyy"
                                    model-type="yyyy-MM-dd"
                                    auto-apply
                                    :clearable="true"
                                    placeholder="Select End Date"
                                    input-class-name="form-control form-control-sm"
                                    :teleport="true"
                                    :min-date="startDate"
                                />
                            </div>

                            <!-- Service Type Filter -->
                            <div class="col-md-3">
                                <label class="form-label">Service Type</label>
                                <vue-multiselect
                                    v-model="selectedServiceType"
                                    :options="serviceTypeOptions"
                                    :searchable="false"
                                    :allow-empty="false"
                                    label="label"
                                    track-by="value"
                                    placeholder="Select service type"
                                    :show-labels="false"
                                ></vue-multiselect>
                            </div>

                            <!-- Customer Filter -->
                            <div class="col-md-3">
                                <label class="form-label">Customer (Optional)</label>
                                <vue-multiselect
                                    v-model="selectedCustomer"
                                    :options="customers"
                                    :searchable="true"
                                    :allow-empty="true"
                                    label="name"
                                    track-by="id"
                                    placeholder="Select Customer"
                                    :show-labels="false"
                                >
                                    <template #option="{ option }">
                                        <div>{{ option.name }}</div>
                                        <div v-if="option.email" class="small">{{ option.email }}</div>
                                    </template>
                                    <template #singleLabel="{ option }">
                                        {{ option.name }} <span v-if="option.email">({{ option.email }})</span>
                                    </template>
                                </vue-multiselect>
                            </div>

                            <!-- Search Button -->
                            <div class="col-md-3">
                                <label class="form-label d-block text-white" style="user-select: none;">&nbsp;</label>
                                <button 
                                    type="button" 
                                    class="btn btn-theme w-100"
                                    :disabled="isLoading"
                                    @click="searchServices"
                                >
                                    <span v-if="isLoading" class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                                    <i v-else class="bi bi-search me-1"></i>
                                    Search
                                </button>
                            </div>
                            
                            <div class="col-12 mt-1">
                                <small class="text-muted">
                                    Services with next due date within the selected range will be listed.
                                </small>
                            </div>
                        </div>
                        <!-- Display hr only if search data available -->
                        <hr v-if="hasSearched" />

                        <!-- Results -->
                        <div v-if="hasSearched">
                            <div class="d-flex justify-content-between align-items-center mb-2">
                                <h6 class="mb-0">Scheduled Services ({{ filteredServices.length }})</h6>
                                <div class="d-flex gap-2">
                                    <input 
                                        type="text" 
                                        class="form-control form-control-sm" 
                                        placeholder="Search in list..." 
                                        v-model="searchQuery"
                                        style="width: 250px;"
                                    >
                                    <button 
                                        type="button" 
                                        class="btn btn-sm btn-outline-theme"
                                        @click="exportCsv"
                                        :disabled="filteredServices.length === 0"
                                    >
                                        <i class="bi bi-download me-1"></i>
                                        Export CSV
                                    </button>
                                </div>
                            </div>

                            <div class="table-responsive" style="max-height: 600px; overflow-y: auto;">
                                <table class="table table-hover table-bordered table-sm align-middle">
                                    <thead class="table-light sticky-top align-middle">
                                        <tr>
                                            <th>Service</th>
                                            <th>Property</th>
                                            <th>Contact Person</th>
                                            <th>Customer</th>
                                            <th>Next Schedule</th>
                                            <th>Frequency</th>
                                            <!-- <th>Status</th> -->
                                            <th>Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-if="filteredServices.length === 0">
                                            <td colspan="7" class="text-center py-3 text-muted">No services found.</td>
                                        </tr>
                                        <tr v-for="service in filteredServices" :key="service.id">
                                            <td>{{ service.service?.name }}</td>
                                            <td>
                                                <small>{{ service.address?.full_address }}</small>
                                            </td>
                                            <td>
                                                <div v-if="service.tenant?.first_name">
                                                    {{ service.tenant.salutation }} {{ service.tenant.first_name }} {{ service.tenant.last_name }}
                                                </div>
                                                <small class="text-muted" v-if="service.tenant?.email">{{ service.tenant.email }}</small>
                                            </td>
                                            <td>
                                                <div>{{ service.customer?.name }}</div>
                                                <small class="text-muted">{{ service.customer?.email }}</small>
                                            </td>
                                            <td>
                                                <div v-if="service.scheduled_date">{{ formatDate(service.scheduled_date) }}</div>
                                                <div v-else>{{ formatDate(service.next_due_date) }}</div>
                                            </td>
                                            <td>{{ formatFrequency(service) }}</td>
                                            <!-- <td>
                                                <span 
                                                    class="badge" 
                                                    :class="{
                                                        'bg-success': service.status === 'active' || service.status === 'completed',
                                                        'bg-danger': service.status === 'inactive' || service.status === 'cancelled',
                                                        'bg-warning': service.status === 'paused' || service.status === 'scheduled'
                                                    }"
                                                >
                                                    {{ service.status }}
                                                </span>
                                            </td> -->
                                            <td>
                                                <a 
                                                    v-if="service.order_number"
                                                    :href="`/admin/orders/${service.order_number}`" 
                                                    class="btn btn-sm btn-outline-theme text-nowrap"
                                                >
                                                    View Order
                                                </a>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                    <!-- <div class="modal-footer">
                        <button type="button" class="btn btn-sm btn-outline-danger" data-bs-dismiss="modal">Close</button>
                    </div> -->
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import moment from 'moment';

export default {
    name: 'DownloadScheduledServices',
    data() {
        return {
            searchRoute: '/api/admin/scheduled-services/list',
            customersRoute: '/api/admin/scheduled-services/customers',
            modal: null,
            isLoading: false,
            hasSearched: false,
            dateRangeOptions: [
                { value: 'choose_month', label: 'Choose Month' },
                { value: 'custom', label: 'Custom' },
                { value: 1, label: '1 Month' },
                { value: 2, label: '2 Months' },
                { value: 3, label: '3 Months' },
                { value: 6, label: '6 Months' },
                { value: 12, label: '1 Year' },
                { value: 24, label: '2 Years' },
                { value: 36, label: '3 Years' },
                { value: 48, label: '4 Years' },
                { value: 60, label: '5 Years' },
            ],
            selectedDateRangeOption: { value: 1, label: '1 Month' },
            serviceTypeOptions: [
                { value: '', label: 'All' },
                { value: 'recurring', label: 'Recurring' },
                //{ value: 'one_time', label: 'One Time' }
            ],
            selectedServiceType: { value: 'recurring', label: 'Recurring' },
            selectedCustomer: null,
            customers: [],
            scheduledServices: [],
            searchQuery: '',
            startDate: '',
            endDate: '',
            selectedMonth: null,
            monthOptions: []
        };
    },
    computed: {
        filteredServices() {
            if (!this.searchQuery) return this.scheduledServices;
            
            const lowerQuery = this.searchQuery.toLowerCase();
            return this.scheduledServices.filter(item => {
                const date = item.scheduled_date ? this.formatDate(item.scheduled_date) : this.formatDate(item.next_due_date);
                return (
                    (item.service?.name || '').toLowerCase().includes(lowerQuery) ||
                    (item.address?.full_address || '').toLowerCase().includes(lowerQuery) ||
                    (item.customer?.name || '').toLowerCase().includes(lowerQuery) ||
                    (item.customer?.email || '').toLowerCase().includes(lowerQuery) ||
                    (date || '').toLowerCase().includes(lowerQuery) ||
                    (item.status || '').toLowerCase().includes(lowerQuery) ||
                    (item.order_number || '').toLowerCase().includes(lowerQuery)
                );
            });
        }
    },
    created() {
        this.selectedDateRangeOption = this.dateRangeOptions.find(option => option.value === 'choose_month');
        
        // Generate month options
        const options = [];
        let date = moment();
        for (let i = 0; i < 12; i++) {
            options.push({
                label: date.format('MMMM YYYY'),
                value: date.format('YYYY-MM-DD')
            });
            date.add(1, 'month');
        }
        this.monthOptions = options;
        this.selectedMonth = this.monthOptions[0];
    },
    mounted() {
        this.modal = new bootstrap.Modal(this.$refs.modalRef);
        this.loadCustomers();
        this.initFilterDownloadButton();
    },
    methods: {
        openModal() {
            this.modal.show();
        },
        async loadCustomers() {
            try {
                const response = await axios.post(this.customersRoute);
                if (response.data.status === 'success') {
                    const customers = response.data.customers || [];
                    // Add "Select Customer" option as the default/reset option
                    this.customers = [{ id: null, name: 'All', email: '' }, ...customers];
                    
                    // Set default selected customer to "Select Customer"
                    this.selectedCustomer = this.customers[0];
                }
            } catch (error) {
                console.error('Failed to load customers:', error);
            }
        },
        formatDate(date) {
            if (!date) return '';
            return moment(date).format('DD/MM/YYYY');
        },
        async searchServices() {
            this.isLoading = true;
            this.hasSearched = true;
            this.scheduledServices = [];
            
            try {
                const response = await axios.post(this.searchRoute, {
                    date_range_months: this.selectedDateRangeOption?.value || '3',
                    service_type: this.selectedServiceType?.value || '',
                    customer_id: this.selectedCustomer?.id || null,
                    month: this.selectedMonth?.value,
                    start_date: this.startDate,
                    end_date: this.endDate
                });
                
                if (response.data.status === 'success') {
                    this.scheduledServices = response.data.scheduled_services || [];
                }
            } catch (error) {
                console.error('Search failed:', error);
                // toastr.error('Failed to fetch scheduled services.');
            } finally {
                this.isLoading = false;
            }
        },
        getOrdinalSuffix(i) {
            const j = i % 10,
                k = i % 100;
            if (j === 1 && k !== 11) {
                return "st";
            }
            if (j === 2 && k !== 12) {
                return "nd";
            }
            if (j === 3 && k !== 13) {
                return "rd";
            }
            return "th";
        },
        formatFrequency(service) {
            const type = service.frequency_type;
            const details = service.frequency_details || {};
            const interval = service.interval || 1;
            
            switch (type) {
                case 'daily':
                    return interval === 1 ? 'Every day' : `Every ${interval} days`;
                case 'weekly':
                    if (details.week_days && details.week_days.length > 0) {
                        const days = details.week_days.map(d => d.charAt(0).toUpperCase() + d.slice(1)).join(', ');
                        return interval === 1 ? `Every week on ${days}` : `Every ${interval} weeks on ${days}`;
                    }
                    return interval === 1 ? 'Every week' : `Every ${interval} weeks`;
                case 'monthly':
                    const day = details.monthly_day || 1;
                    return interval === 1 ? `Every month on the ${day}${this.getOrdinalSuffix(day)}` : `Every ${interval} months on the ${day}${this.getOrdinalSuffix(day)}`;
                case 'yearly':
                    const mName = moment().month((details.yearly_month || 1) - 1).format('MMMM');
                    const yDay = details.yearly_day || 1;
                    return interval === 1 ? `Every year on ${mName} ${yDay}${this.getOrdinalSuffix(yDay)}` : `Every ${interval} years on ${mName} ${yDay}${this.getOrdinalSuffix(yDay)}`;
                default:
                    return type.charAt(0).toUpperCase() + type.slice(1);
            }
        },
        exportCsv() {
            if (this.filteredServices.length === 0) return;
            this.generateCsvFromData(this.filteredServices);
        },
        generateCsvFromData(services) {
            if (services.length === 0) return;

            // Define headers
            const headers = [
                'Service', 
                'Property', 
                'Tenant Title', 'Tenant First Name', 'Tenant Last Name', 'Tenant Email', 'Tenant Phone Number', 'Tenant Phone Number 2',
                'Customer Name', 'Customer Email', 'Customer Phone',
                'Next Schedule', 
                'Frequency', 
                'Order Number'
            ];
            
            // Map data
            const rows = services.map(item => [
                item.service?.name || '',
                (item.address?.full_address || '').replace(/,/g, ' '), 
                
                // Tenant Details
                item.tenant?.salutation || '',
                item.tenant?.first_name || '',
                item.tenant?.last_name || '',
                item.tenant?.email || '',
                item.tenant?.phone_number || '',
                item.tenant?.phone_number_2 || '',

                // Customer Details
                (item.customer?.name || '').replace(/,/g, ' '), 
                item.customer?.email || '',
                item.customer?.phone_number || '',

                item.scheduled_date ? this.formatDate(item.scheduled_date) : this.formatDate(item.next_due_date),
                this.formatFrequency(item).replace(/,/g, ' '),
                item.order_number || ''
            ]);
            
            // Build CSV string
            let csvContent = "data:text/csv;charset=utf-8," 
                + headers.join(",") + "\n" 
                + rows.map(e => e.join(",")).join("\n");
                
            const encodedUri = encodeURI(csvContent);
            const link = document.createElement("a");
            link.setAttribute("href", encodedUri);
            const date = new Date().toISOString().split('T')[0];
            link.setAttribute("download", `scheduled-services-export-${date}.csv`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        },
        initFilterDownloadButton() {
            // Listen for click on the filter download button using jQuery
            const self = this;
            $(document).on('click', '#download-scheduled-services-filter', function() {
                self.downloadFromFilter();
            });
        },
        async downloadFromFilter() {
            // Get filter values from the filter form using jQuery
            const dateRangeMonths = $('#scheduled-service-filter-form select[name="date_range_months"]').val() || '3';
            const serviceType = $('#scheduled-service-filter-form select[name="service_type"]').val() || '';
            const serviceId = $('#scheduled-service-filter-form select[name="service_id"]').val() || '';
            const customerId = $('#scheduled-service-filter-form select[name="customer_id"]').val() || '';
            const propertyId = $('#scheduled-service-filter-form select[name="property_id"]').val() || '';
            const status = $('#scheduled-service-filter-form select[name="status"]').val() || '';
            const startDate = $('#scheduled-service-filter-form input[name="start_date"]').val() || '';
            const endDate = $('#scheduled-service-filter-form input[name="end_date"]').val() || '';
            const month = $('#scheduled-service-filter-form select[name="selected_month"]').val() || '';

            try {
                // Show loading state on button
                const $btn = $('#download-scheduled-services-filter');
                const originalHtml = $btn.html();
                $btn.html('<span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>').prop('disabled', true);

                const response = await axios.post(this.searchRoute, {
                    date_range_months: dateRangeMonths,
                    service_type: serviceType,
                    services: serviceId ? [serviceId] : [],
                    customer_id: customerId || null,
                    address_id: propertyId || null,
                    status: status || 'active',
                    start_date: startDate,
                    end_date: endDate,
                    month: month
                });

                if (response.data.status === 'success') {
                    const services = response.data.scheduled_services || [];
                    if (services.length > 0) {
                        this.generateCsvFromData(services);
                    } else {
                        toastr.warning('No scheduled services found with the selected filters.');
                    }
                }

                // Restore button state
                $btn.html(originalHtml).prop('disabled', false);
            } catch (error) {
                console.error('Download failed:', error);
                toastr.error('Failed to download scheduled services.');
                // Restore button state
                $('#download-scheduled-services-filter').html('<i class="bi bi-download"></i> Download').prop('disabled', false);
            }
        }
    }
}
</script>
